// web/src/pages/admin/SpamAddressesPage.tsx
//
// T20 — Admin CRUD + bulk import for spam email address patterns
// (`/api/admin/spam-addresses`). Modeled on ApprovalTypesPage.tsx.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { spamAddressesApi, type SpamAddress } from "@/lib/spam-addresses";
import { ApiError } from "@/lib/api";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailModal } from "@/components/detail-modal";
import { RowActions } from "@/components/row-actions";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useConfirm } from "@/hooks/use-confirm";
import { toast } from "@/hooks/use-toast";

function serverMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const detail = (err.body as { detail?: string } | null)?.detail;
    if (detail) return detail;
  }
  return fallback;
}

export function SpamAddressesPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const [addOpen, setAddOpen] = useState(false);
  const [pattern, setPattern] = useState("");

  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [rejectedLines, setRejectedLines] = useState<string[]>([]);

  const { data: addresses = [], isLoading } = useQuery({
    queryKey: ["spam-addresses"],
    queryFn: () => spamAddressesApi.list(),
  });

  function closeAdd() {
    setAddOpen(false);
    setPattern("");
  }

  function closeImport() {
    setImportOpen(false);
    setImportText("");
    setRejectedLines([]);
  }

  const create = useMutation({
    mutationFn: (p: string) => spamAddressesApi.create(p),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["spam-addresses"] });
      toast.success("Spam address added.");
      closeAdd();
    },
    onError: (err: unknown) => toast.error(serverMessage(err, "Add failed.")),
  });

  const del = useMutation({
    mutationFn: (id: number) => spamAddressesApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["spam-addresses"] });
      toast.success("Spam address deleted.");
    },
    onError: (err: unknown) => toast.error(serverMessage(err, "Delete failed.")),
  });

  const bulkImport = useMutation({
    mutationFn: (text: string) => spamAddressesApi.import(text),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ["spam-addresses"] });
      toast.success(`${result.accepted} added, ${result.rejected.length} rejected`);
      if (result.rejected.length > 0) {
        setRejectedLines(result.rejected);
      } else {
        closeImport();
      }
    },
    onError: (err: unknown) => toast.error(serverMessage(err, "Import failed.")),
  });

  async function handleDelete(row: SpamAddress) {
    const ok = await confirm({
      title: "Delete spam address?",
      body: row.pattern,
      confirmLabel: "Delete",
      intent: "destructive",
    });
    if (ok) del.mutate(row.spamEmailAddressId);
  }

  const cols: DataGridColumn<SpamAddress>[] = [
    { id: "pattern", header: "Pattern", accessor: (r) => r.pattern },
    {
      id: "actions",
      header: "",
      accessor: (r) => (
        <div className="flex justify-end pr-2">
          <RowActions
            onDelete={() => void handleDelete(r)}
            deleteDisabled={del.isPending}
            deleteTooltip="Delete Spam Address"
          />
        </div>
      ),
      align: "right",
    },
  ];

  return (
    <>
      <ListPage backTo="/admin"
        title="Spam Addresses"
        primaryAction={
          <div className="flex gap-2">
            <IntentButton
              intent="secondary"
              onClick={() => setImportOpen(true)}
              aria-label="Bulk import"
            >
              Bulk import
            </IntentButton>
            <IntentButton onClick={() => setAddOpen(true)} aria-label="Add pattern">
              <Plus className="mr-2 h-4 w-4" /> Add Pattern
            </IntentButton>
          </div>
        }
      >
        <DataGrid
          rows={addresses}
          columns={cols}
          rowKey={(r) => r.spamEmailAddressId}
          totalRows={addresses.length}
          pageIndex={0}
          pageSize={addresses.length || 1}
          loading={isLoading}
          emptyHeadline="No spam addresses found."
          onPageChange={() => void 0}
        />
      </ListPage>

      <DetailModal
        open={addOpen}
        onOpenChange={(o) => {
          if (!o) closeAdd();
        }}
        title="Add Spam Pattern"
      >
        <div className="space-y-4 p-4">
          <div>
            <Label htmlFor="spam-pattern">Pattern *</Label>
            <Input
              id="spam-pattern"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder="e.g. spammer@bad.com or *@spamdomain.com"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Exact address or *@domain
            </p>
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              onClick={() => create.mutate(pattern)}
              disabled={create.isPending || !pattern.trim()}
            >
              {create.isPending ? "Saving…" : "Save"}
            </Button>
            <Button variant="outline" onClick={closeAdd}>
              Cancel
            </Button>
          </div>
        </div>
      </DetailModal>

      <DetailModal
        open={importOpen}
        onOpenChange={(o) => {
          if (!o) closeImport();
        }}
        title="Bulk Import Spam Patterns"
      >
        <div className="space-y-4 p-4">
          <div>
            <Label htmlFor="spam-import-text">Patterns *</Label>
            <Textarea
              id="spam-import-text"
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder={"One pattern per line\ne.g. spammer@bad.com\n*@spamdomain.com"}
              rows={8}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              One pattern per line — exact address or *@domain
            </p>
          </div>
          {rejectedLines.length > 0 && (
            <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">
              <p className="font-medium">Rejected lines:</p>
              <ul className="list-disc pl-4">
                {rejectedLines.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button
              onClick={() => bulkImport.mutate(importText)}
              disabled={bulkImport.isPending || !importText.trim()}
            >
              {bulkImport.isPending ? "Importing…" : "Import"}
            </Button>
            <Button variant="outline" onClick={closeImport}>
              Cancel
            </Button>
          </div>
        </div>
      </DetailModal>
    </>
  );
}
